import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Inbox, RefreshCw } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { formatMoney, fromString } from '@aeris/money'
import {
  Badge,
  Button,
  EmptyState,
  Page,
  PageHeader,
  Select,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { LoadFailed } from '../components/load-failed.tsx'
import {
  ApiError,
  acceptChannelOrder,
  fetchChannelOrders,
  fetchChannelOrdersFromMarketplace,
  fetchSalesChannels,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { formatDayTime } from '../lib/date-format.ts'

/**
 * Pedidos dos canais — the marketplace inbox (F032).
 *
 * The screen the product owner asked for by name: *ver pedido*, and then *ver
 * vendas*. Those are two states of one row, which is why they are one screen —
 * an accepted order carries a link to the sale it became, so the question "did
 * this actually turn into anything?" is answered where it is asked.
 *
 * The column that earns its place is **Situação**, and specifically what a
 * failure says. An order that did not convert has a sentence on it — "this
 * advert is not linked", "no branch holds all of it" — because the person
 * reading this list is the person who will go and fix it, and a status of
 * "falhou" would send them to a log they cannot read.
 */

const STATUS_LABELS: Record<string, string> = {
  pending: 'Aguardando',
  accepted: 'Virou venda',
  failed: 'Não converteu',
  ignored: 'Ignorado',
}

const STATUS_TONES: Record<string, 'neutral' | 'success' | 'danger'> = {
  pending: 'neutral',
  accepted: 'success',
  failed: 'danger',
  ignored: 'neutral',
}

const money = (value: string): string => formatMoney(fromString(value))

export const PedidosCanalPage = (): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()
  const canWrite = allows('channel.write')

  const [status, setStatus] = useState('pending')
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const channels = useQuery({
    queryKey: ['sales-channels'],
    queryFn: async ({ signal }) => await fetchSalesChannels(undefined, signal),
  })

  const orders = useQuery({
    queryKey: ['channel-orders', status],
    queryFn: async ({ signal }) =>
      await fetchChannelOrders(status === 'all' ? {} : { status }, signal),
  })

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['channel-orders'] })
  }

  const accept = useMutation({
    mutationFn: async (orderId: string) => {
      await acceptChannelOrder(orderId)
    },
    onSuccess: async () => {
      setError(null)
      await refresh()
    },
    onError: async (cause: unknown) => {
      // The refusal is also written on the row, so the list is refetched: the
      // toast disappears and the reason has to survive somewhere durable.
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível aceitar este pedido.'
      )
      await refresh()
    },
  })

  /**
   * A rede de proteção embaixo do webhook: pedir ao marketplace agora.
   *
   * Desde a F109 a busca é um job — a rota enfileira e responde. Por isso a
   * tela diz "enfileirada" em vez de recarregar a lista fingindo que o
   * resultado já está nela: recarregar aqui mostraria exatamente o que já
   * estava na tela, e a pessoa concluiria que não veio pedido nenhum.
   */
  const pull = useMutation({
    // Todos os canais ligados, e não o primeiro: a loja com dois marketplaces
    // nunca buscava o segundo por aqui.
    mutationFn: async (channelIds: readonly string[]) => {
      await Promise.all(
        channelIds.map(
          async (channelId) =>
            await fetchChannelOrdersFromMarketplace(channelId)
        )
      )
    },
    onSuccess: () => {
      setError(null)
      setNotice(
        'Busca enfileirada. Os pedidos aparecem aqui assim que chegarem — atualize em instantes.'
      )
    },
    onError: (cause: unknown) => {
      setNotice(null)
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível buscar pedidos agora.'
      )
    },
  })

  const rows = orders.data?.orders ?? []
  const connected = (channels.data?.channels ?? []).filter(
    (channel) => channel.status === 'active'
  )

  return (
    <Page>
      <PageHeader
        title="Pedidos dos canais"
        description="O que os marketplaces venderam, e o que já virou venda aqui dentro."
        actions={
          canWrite && connected.length > 0 ? (
            <Button
              variant="secondary"
              disabled={pull.isPending}
              onClick={() => {
                pull.mutate(connected.map((channel) => channel.id))
              }}
            >
              <RefreshCw aria-hidden="true" /> Buscar no canal
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value)
          }}
          aria-label="Situação"
          className="w-52"
        >
          <option value="pending">Aguardando</option>
          <option value="failed">Não converteram</option>
          <option value="accepted">Viraram venda</option>
          <option value="all">Todos</option>
        </Select>
      </div>

      {error === null ? null : (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {error}
        </p>
      )}

      {notice === null ? null : (
        <p
          role="status"
          className="text-(length:--text-sm) text-(--text-muted)"
        >
          {notice}
        </p>
      )}

      {orders.isPending ? (
        <Spinner />
      ) : orders.isError ? (
        <LoadFailed
          what="os pedidos dos canais"
          onRetry={() => {
            void orders.refetch()
          }}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Inbox aria-hidden="true" />}
          title="Nenhum pedido aqui"
          description="Pedidos aparecem quando o marketplace avisa, ou quando você usa “Buscar no canal”."
        />
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Pedido</TableHeaderCell>
                <TableHeaderCell>Canal</TableHeaderCell>
                <TableHeaderCell>Comprador</TableHeaderCell>
                <TableHeaderCell>Itens</TableHeaderCell>
                <TableHeaderCell>Total</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((order) => (
                <TableRow key={order.id}>
                  <TableCell className="font-(--font-mono)">
                    {order.externalOrderId}
                    <span className="block text-(length:--text-xs) text-(--text-muted)">
                      {formatDayTime(order.placedAt)}
                    </span>
                  </TableCell>
                  <TableCell>{order.channelName}</TableCell>
                  <TableCell>{order.buyerName ?? '—'}</TableCell>
                  <TableCell>
                    <ul className="flex flex-col gap-0.5">
                      {order.lines.map((line) => (
                        <li key={line.id} className="text-(length:--text-xs)">
                          {line.quantity.replace(/\.?0+$/, '')}×{' '}
                          {line.description}
                          {/* The one fact that decides whether this can convert. */}
                          {line.variantId === null ? (
                            <span className="text-(--danger)">
                              {' '}
                              — anúncio não ligado
                            </span>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </TableCell>
                  <TableCell className="tabular-nums">
                    {money(order.totalAmount)}
                  </TableCell>
                  <TableCell>
                    <Badge tone={STATUS_TONES[order.status] ?? 'neutral'}>
                      {STATUS_LABELS[order.status] ?? order.status}
                    </Badge>
                    {order.failureReason === null ? null : (
                      <span className="mt-1 block text-(length:--text-xs) text-(--text-muted)">
                        {order.failureReason}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {order.salesDocumentId !== null ? (
                      // "Ver vendas", from where the question is asked.
                      <Link
                        to="/vendas"
                        className="text-(length:--text-sm) underline-offset-2 hover:underline"
                      >
                        Ver a venda
                      </Link>
                    ) : canWrite && order.status !== 'accepted' ? (
                      <Button
                        size="sm"
                        disabled={accept.isPending}
                        aria-label={`Aceitar o pedido ${order.externalOrderId}`}
                        onClick={() => {
                          accept.mutate(order.id)
                        }}
                      >
                        Aceitar
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Page>
  )
}
